/**
 * Key Slide-over Alpine.js Component
 *
 * Manages the key bunch detail slide-over panel state and data fetching.
 * Displays key details, status, checkout history, and notes timeline.
 * Supports inline editing, checkout/checkin, and note management.
 */

import consumer from "../channels/consumer"
import { notesMixin, destroyNote } from './notes_mixin'

export function keySlideover() {
  return {
    ...notesMixin,
    // State
    open: false,
    loading: false,
    error: null,
    keyBunch: null,
    keyId: null,
    propertyId: null,
    subscription: null,
    zIndex: 100010,

    // Edit mode state
    editMode: false,
    editIdentifier: '',
    editNotes: '',
    editingNotes: false,
    editNotesText: '',
    showEditModal: false,
    editDetailIdentifier: '',
    editDetailNotes: '',
    saving: false,
    saveError: null,

    // Note form state
    noteText: '',
    noteSaving: false,
    noteError: null,

    // Checkout form state (for inline checkout from slideover)
    showCheckoutForm: false,
    checkoutSaving: false,
    checkoutError: null,
    checkoutForm: {
      checked_out_to: '',
      contact_phone: '',
      contact_email: '',
      expected_return_at: ''
    },

    // People search for checkout
    showPeopleDropdown: false,
    peopleResults: [],

    // Expected return edit state
    showExpectedReturnModal: false,
    editExpectedReturnDate: '',
    expectedReturnSaving: false,
    expectedReturnError: null,

    // Add Key state
    showAddKeyModal: false,
    addKeySuggestions: [],
    addKeyIdentifier: '',
    addKeyNotes: '',
    addKeyIdentifierError: null,
    addKeySaving: false,
    addKeyError: null,

    // Permanent checkout state (keys handed over for good, e.g. back to landlord)
    showPermanentCheckoutModal: false,
    permanentCheckoutForm: { given_to: '', notes: '' },
    permanentCheckoutSaving: false,
    permanentCheckoutError: null,
    showPermPeopleDropdown: false,
    permPeopleResults: [],

    // Action states
    actionSaving: false,
    actionError: null,

    // Tab navigation
    activeTab: 'overview',

    // Kebab menu
    kebabOpen: false,

    // Initialize component
    init() {
      // CRITICAL: Capture Alpine context for ActionCable callbacks
      const self = this

      // Subscribe to ActionCable for real-time updates
      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            // Handle key bunch updates
            if (message.type === 'key_bunch' && self.keyBunch && message.id === self.keyBunch.id) {
              console.debug('[KeySlideover] Received key_bunch update', message.id)
              self.handleKeyBunchUpdate(message)
            }
            // Handle key log updates
            if (message.type === 'key_log' && self.keyBunch && message.data?.key_bunch_id === self.keyBunch.id) {
              console.debug('[KeySlideover] Received key_log update for key', self.keyBunch.id)
              // Refresh to get updated timeline
              self.fetchKeyBunch()
            }
            // Handle key note updates
            if (message.type === 'property_key_note' && self.keyBunch && message.data?.key_bunch_id === self.keyBunch.id) {
              console.debug('[KeySlideover] Received property_key_note update for key', self.keyBunch.id)
              self.handleNoteUpdate(message)
            }
          }
        }
      )

      // Listen for open requests
      this.$watch('open', (value) => {
        if (value && this.keyId && !this.keyBunch) {
          this.fetchKeyBunch()
        }
      })
    },

    // Open slide-over for a specific key
    // Options: { showCheckout: boolean } - if true, opens with checkout form expanded
    openForKey(keyId, propertyId, options = {}) {
      const store = Alpine.store?.('slideovers')

      // If this key is already open, just bring it to front
      if (store?.isOpen?.('key', keyId)) {
        store.bringToFront('key', keyId)
        // If showCheckout requested, open the form
        if (options.showCheckout) {
          this.showCheckoutForm = true
        }
        return
      }

      // Get next z-index from store for proper stacking
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.keyId = keyId
      this.propertyId = propertyId
      this.keyBunch = null
      this.error = null
      this.activeTab = 'overview'
      this.kebabOpen = false
      this.resetEditState()
      this.resetNoteState()
      this.resetCheckoutForm()

      // If showCheckout requested, open the form after data loads
      this.pendingShowCheckout = options.showCheckout || false

      this.open = true

      // Register this slideover
      if (store?.register) {
        store.register('key', keyId, this)
      }

      this.fetchKeyBunch()
    },

    // Close slide-over
    close() {
      // Unregister from store
      const store = Alpine.store?.('slideovers')
      if (store?.unregister && this.keyId) {
        store.unregister('key', this.keyId)
      }

      // Reset all state
      this.activeTab = 'overview'
      this.kebabOpen = false
      this.resetEditState()
      this.resetNoteState()
      this.resetCheckoutForm()

      this.$nextTick(() => {
        this.open = false
      })
    },

    // Reset editing state
    resetEditState() {
      this.editMode = false
      this.editIdentifier = ''
      this.editNotes = ''
      this.editingNotes = false
      this.editNotesText = ''
      this.showEditModal = false
      this.editDetailIdentifier = ''
      this.editDetailNotes = ''
      this.saving = false
      this.saveError = null
      this.showExpectedReturnModal = false
      this.editExpectedReturnDate = ''
      this.expectedReturnSaving = false
      this.expectedReturnError = null
      this.showPermanentCheckoutModal = false
      this.permanentCheckoutForm = { given_to: '', notes: '' }
      this.permanentCheckoutSaving = false
      this.permanentCheckoutError = null
      this.showPermPeopleDropdown = false
      this.permPeopleResults = []
    },

    // Reset note form state
    resetNoteState() {
      this.noteText = ''
      this.noteSaving = false
      this.noteError = null
    },

    // Reset checkout form
    resetCheckoutForm() {
      this.showCheckoutForm = false
      this.checkoutSaving = false
      this.checkoutError = null
      this.checkoutForm = {
        checked_out_to: '',
        contact_phone: '',
        contact_email: '',
        expected_return_at: ''
      }
      this.showPeopleDropdown = false
      this.peopleResults = []
    },

    // Fetch key bunch data from server
    async fetchKeyBunch() {
      if (!this.keyId || !this.propertyId) return

      this.loading = true
      this.error = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        this.keyBunch = await response.json()

        // If pending checkout form request and key is not already checked out, show the form
        if (this.pendingShowCheckout && !this.keyBunch.checked_out && this.keyBunch.status !== 'missing') {
          this.showCheckoutForm = true
        }
        this.pendingShowCheckout = false
      } catch (error) {
        console.error('Failed to fetch key bunch:', error)
        this.error = 'Failed to load key details. Please try again.'
      } finally {
        this.loading = false
      }
    },

    // Handle real-time updates from ActionCable
    handleKeyBunchUpdate(message) {
      if (!this.keyBunch || message.id !== this.keyBunch.id) return

      switch (message.action) {
        case 'update':
          // Merge updated data (keep timeline if not in message)
          const timeline = this.keyBunch.timeline
          Object.assign(this.keyBunch, message.data)
          if (!message.data.timeline) {
            this.keyBunch.timeline = timeline
          }
          break
        case 'destroy':
          this.close()
          break
      }
    },

    // Handle note updates from ActionCable
    handleNoteUpdate(message) {
      if (!this.keyBunch?.timeline) return

      switch (message.action) {
        case 'create':
          // Add new note to timeline (at the beginning since it's most recent)
          this.keyBunch.timeline.unshift(message.data)
          break
        case 'destroy':
          // Remove note from timeline
          this.keyBunch.timeline = this.keyBunch.timeline.filter(item =>
            !(item.type === 'note' && item.id === message.id)
          )
          break
      }
    },

    // Open the edit details modal
    openEditModal() {
      this.editDetailIdentifier = this.keyBunch?.identifier || ''
      this.editDetailNotes = this.keyBunch?.notes || ''
      this.showEditModal = true
    },

    // Save key details (identifier + notes)
    async saveDetails() {
      if (this.saving) return

      this.saving = true

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            property_key_bunch: {
              identifier: this.editDetailIdentifier.trim(),
              notes: this.editDetailNotes.trim()
            }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        const updated = await response.json()
        const timeline = this.keyBunch.timeline
        this.keyBunch = { ...updated, timeline }
        this.showEditModal = false
      } catch (error) {
        console.error('Failed to save details:', error)
      } finally {
        this.saving = false
      }
    },

    // Open the add key form
    openAddKeyForm() {
      this.addKeyIdentifier = ''
      this.addKeyNotes = ''
      this.addKeyIdentifierError = null
      this.addKeyError = null
      this.showAddKeyModal = true
      this.loadAddKeySuggestions()
    },

    // The first free tag number in each series the office uses
    async loadAddKeySuggestions() {
      try {
        const response = await fetch('/keys/next_identifiers', { headers: { 'Accept': 'application/json' } })
        const data = await response.json()
        this.addKeySuggestions = data.suggestions || []
      } catch (error) {
        this.addKeySuggestions = []
      }
    },

    useAddKeySuggestion(suggestion) {
      this.addKeyIdentifier = suggestion
      this.checkNewKeyIdentifier()
    },

    // Check if new key identifier is available
    async checkNewKeyIdentifier() {
      const identifier = this.addKeyIdentifier.trim()
      if (!identifier) {
        this.addKeyIdentifierError = null
        return
      }

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/check_identifier?identifier=${encodeURIComponent(identifier)}`)
        const data = await response.json()
        this.addKeyIdentifierError = data.available ? null : (data.message || 'This identifier is already in use')
      } catch (error) {
        // Don't block on validation failure
        this.addKeyIdentifierError = null
      }
    },

    // Save a new key bunch
    async saveNewKey() {
      if (this.addKeySaving || !this.addKeyIdentifier.trim() || this.addKeyIdentifierError) return

      this.addKeySaving = true
      this.addKeyError = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            property_key_bunch: {
              identifier: this.addKeyIdentifier.trim(),
              notes: this.addKeyNotes.trim()
            }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.join(', ') || 'Failed to add key')
        }

        const newKey = await response.json()
        this.showAddKeyModal = false

        // Navigate to the newly created key
        this.openForKey(newKey.id, this.propertyId)
      } catch (error) {
        this.addKeyError = error.message
      } finally {
        this.addKeySaving = false
      }
    },

    // Save notes inline
    async saveNotes() {
      if (this.saving) return

      this.saving = true

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            property_key_bunch: {
              notes: this.editNotesText.trim()
            }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        const updated = await response.json()
        const timeline = this.keyBunch.timeline
        this.keyBunch = { ...updated, timeline }
        this.editingNotes = false
      } catch (error) {
        console.error('Failed to save notes:', error)
      } finally {
        this.saving = false
      }
    },

    // Start editing mode
    startEditing() {
      this.editIdentifier = this.keyBunch?.identifier || ''
      this.editNotes = this.keyBunch?.notes || ''
      this.editMode = true
      this.saveError = null
    },

    // Cancel editing
    cancelEditing() {
      this.resetEditState()
    },

    // Save key bunch changes
    async saveChanges() {
      if (this.saving) return

      this.saving = true
      this.saveError = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            property_key_bunch: {
              identifier: this.editIdentifier.trim(),
              notes: this.editNotes.trim()
            }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        const updated = await response.json()
        // Preserve timeline
        const timeline = this.keyBunch.timeline
        this.keyBunch = { ...updated, timeline }
        this.editMode = false
      } catch (error) {
        console.error('Failed to save key bunch:', error)
        this.saveError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.saving = false
      }
    },

    // Add a note to this key
    async addNote() {
      if (!this.noteText.trim() || this.noteSaving) return

      this.noteSaving = true
      this.noteError = null

      try {
        const newNote = await this.saveNote({ type: 'PropertyKeyBunch', id: this.keyId }, this.noteText)
        if (this.keyBunch) {
          if (!this.keyBunch.timeline) this.keyBunch.timeline = []
          if (!this.keyBunch.timeline.some(e => e.id === newNote.id)) this.keyBunch.timeline.unshift(newNote)
        }
        this.noteText = ''
      } catch (error) {
        console.error('Failed to add note:', error)
        this.noteError = error.message || 'Failed to add note. Please try again.'
      } finally {
        this.noteSaving = false
      }
    },
    removeTimelineEntry(entryId) {
      if (this.keyBunch?.timeline) {
        this.keyBunch.timeline = this.keyBunch.timeline.filter(e => e.id !== entryId)
      }
    },

    // Delete a note
    async deleteNote(noteId) {
      if (!confirm('Delete this note?')) return
      try {
        await destroyNote(noteId)
        this.removeTimelineEntry(noteId)
      } catch (error) {
        console.error('Failed to delete note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    // Check in keys
    async checkIn() {
      if (!this.keyBunch?.current_checkout) return

      this.actionSaving = true
      this.actionError = null

      try {
        const checkoutId = this.keyBunch.current_checkout.id

        const response = await fetch(
          `/properties/${this.propertyId}/keys/${this.keyId}/checkouts/${checkoutId}/check_in`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to check in keys')
        }

        // Refresh to get updated data and timeline
        await this.fetchKeyBunch()
      } catch (error) {
        console.error('Failed to check in keys:', error)
        this.actionError = error.message || 'Failed to check in keys.'
      } finally {
        this.actionSaving = false
      }
    },

    // Open the expected return date modal
    openExpectedReturnModal() {
      this.editExpectedReturnDate = this.keyBunch?.current_checkout?.expected_return_at || ''
      this.expectedReturnError = null
      this.showExpectedReturnModal = true
    },

    // Update expected return date
    async updateExpectedReturn() {
      if (this.expectedReturnSaving || !this.editExpectedReturnDate) return

      this.expectedReturnSaving = true
      this.expectedReturnError = null

      try {
        const checkoutId = this.keyBunch.current_checkout.id

        const response = await fetch(
          `/properties/${this.propertyId}/keys/${this.keyId}/checkouts/${checkoutId}/update_expected_return`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            },
            body: JSON.stringify({
              expected_return_at: this.editExpectedReturnDate
            })
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to update expected return date')
        }

        // Refresh to get updated data and timeline
        await this.fetchKeyBunch()
        this.showExpectedReturnModal = false
      } catch (error) {
        console.error('Failed to update expected return date:', error)
        this.expectedReturnError = error.message || 'Failed to update expected return date.'
      } finally {
        this.expectedReturnSaving = false
      }
    },

    // Open checkout form
    openCheckoutForm() {
      this.resetCheckoutForm()
      this.showCheckoutForm = true
    },

    // Search for people (for checkout autocomplete)
    async searchPeople() {
      const query = this.checkoutForm.checked_out_to.trim()

      if (query.length < 2) {
        this.peopleResults = []
        this.showPeopleDropdown = false
        return
      }

      try {
        const response = await fetch(`/api/people/search?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json' }
        })

        if (response.ok) {
          this.peopleResults = await response.json()
          this.showPeopleDropdown = this.peopleResults.length > 0
        }
      } catch (error) {
        console.error('Error searching people:', error)
      }
    },

    // Select a person from search results
    selectPerson(person) {
      this.checkoutForm.checked_out_to = person.name
      this.checkoutForm.contact_email = person.email || ''
      this.checkoutForm.contact_phone = person.phone || ''
      this.showPeopleDropdown = false
      this.peopleResults = []
    },

    // Check out keys
    async checkOut() {
      if (this.checkoutSaving) return

      this.checkoutSaving = true
      this.checkoutError = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}/checkouts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            property_key_checkout: this.checkoutForm
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to check out keys')
        }

        // Refresh to get updated data and timeline
        await this.fetchKeyBunch()
        this.resetCheckoutForm()
      } catch (error) {
        console.error('Failed to check out keys:', error)
        this.checkoutError = error.message || 'Failed to check out keys.'
      } finally {
        this.checkoutSaving = false
      }
    },

    // Mark as missing
    async markMissing() {
      if (!confirm('Mark this key as missing?')) return

      this.actionSaving = true
      this.actionError = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}/mark_missing`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error('Failed to mark key as missing')
        }

        // Refresh to get updated data
        await this.fetchKeyBunch()
      } catch (error) {
        console.error('Failed to mark key as missing:', error)
        this.actionError = error.message || 'Failed to mark key as missing.'
      } finally {
        this.actionSaving = false
      }
    },

    // Mark as found
    async markFound() {
      this.actionSaving = true
      this.actionError = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}/mark_found`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error('Failed to mark key as found')
        }

        // Refresh to get updated data
        await this.fetchKeyBunch()
      } catch (error) {
        console.error('Failed to mark key as found:', error)
        this.actionError = error.message || 'Failed to mark key as found.'
      } finally {
        this.actionSaving = false
      }
    },

    // Delete key
    async deleteKey() {
      if (!confirm('Are you sure you want to delete this key permanently? This action cannot be reversed, but a log entry will be retained for your records.')) {
        return
      }

      this.actionSaving = true
      this.actionError = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error('Failed to delete key')
        }

        this.close()
      } catch (error) {
        console.error('Failed to delete key:', error)
        this.actionError = error.message || 'Failed to delete key.'
      } finally {
        this.actionSaving = false
      }
    },

    // Open the permanent checkout modal (keys handed over for good)
    openPermanentCheckoutModal() {
      this.permanentCheckoutForm = { given_to: '', notes: '' }
      this.permanentCheckoutError = null
      this.showPermPeopleDropdown = false
      this.permPeopleResults = []
      this.showPermanentCheckoutModal = true
    },

    // Search for people (for permanent checkout "given to" autocomplete)
    async searchPeopleForPermanent() {
      const query = this.permanentCheckoutForm.given_to.trim()

      if (query.length < 2) {
        this.permPeopleResults = []
        this.showPermPeopleDropdown = false
        return
      }

      try {
        const response = await fetch(`/api/people/search?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json' }
        })

        if (response.ok) {
          this.permPeopleResults = await response.json()
          this.showPermPeopleDropdown = this.permPeopleResults.length > 0
        }
      } catch (error) {
        console.error('Error searching people:', error)
      }
    },

    // Select a person from the permanent checkout search results
    selectPersonForPermanent(person) {
      this.permanentCheckoutForm.given_to = person.name
      this.showPermPeopleDropdown = false
      this.permPeopleResults = []
    },

    // Permanently check out the keys — records who they went to, transfers the key
    // (frees its number, drops it from the active list) and keeps the full history.
    async checkoutPermanently() {
      if (this.permanentCheckoutSaving || !this.permanentCheckoutForm.given_to.trim()) return

      this.permanentCheckoutSaving = true
      this.permanentCheckoutError = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.keyId}/checkout_permanently`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            given_to: this.permanentCheckoutForm.given_to.trim(),
            notes: this.permanentCheckoutForm.notes.trim()
          })
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.errors?.[0] || 'Failed to check out keys permanently')
        }

        this.showPermanentCheckoutModal = false
        // The key has left the active list — close the slideover.
        this.close()
      } catch (error) {
        console.error('Failed to check out keys permanently:', error)
        this.permanentCheckoutError = error.message || 'Failed to check out keys permanently.'
      } finally {
        this.permanentCheckoutSaving = false
      }
    },

    // Computed: timeline count for History tab badge
    get timelineCount() {
      return this.keyBunch?.timeline?.length || 0
    },

    // Computed: status badge class
    get statusClass() {
      if (!this.keyBunch) return ''

      const status = this.keyBunch.status_display
      const classes = {
        'Checked In': 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        'Checked Out': 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        'Overdue': 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        'Missing': 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        'Permanently Checked Out': 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300'
      }
      return classes[status] || ''
    },

    // Computed: is key checked out?
    get isCheckedOut() {
      return this.keyBunch?.checked_out === true
    },

    // Computed: is key missing?
    get isMissing() {
      return this.keyBunch?.status === 'missing'
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },

    // Get relative date string (e.g. "today", "in 3 days", "2 days ago")
    getRelativeDate(dateString) {
      if (!dateString) return ''

      const date = new Date(dateString)
      const today = new Date()

      today.setHours(0, 0, 0, 0)
      date.setHours(0, 0, 0, 0)

      const diffTime = date - today
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'today'
      if (diffDays === 1) return 'tomorrow'
      if (diffDays === -1) return 'yesterday'
      if (diffDays > 1 && diffDays <= 7) return `in ${diffDays} days`
      if (diffDays > 7 && diffDays <= 14) return 'next week'
      if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`
      if (diffDays < -7 && diffDays >= -14) return 'last week'
      if (diffDays > 14) return `in ${Math.ceil(diffDays / 7)} weeks`
      if (diffDays < -14) return `${Math.ceil(Math.abs(diffDays) / 7)} weeks ago`

      return ''
    },

    // Get initials from a name (e.g. "John Smith" -> "JS")
    getInitials(name) {
      if (!name) return '?'
      name = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
      return name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    },

    // Get timeline icon color class
    getTimelineColorClass(color) {
      const colors = {
        blue: 'bg-blue-500',
        green: 'bg-green-500',
        purple: 'bg-purple-500',
        gray: 'bg-gray-400',
        red: 'bg-red-500',
        orange: 'bg-orange-500',
        yellow: 'bg-yellow-500'
      }
      return colors[color] || 'bg-gray-400'
    }
  }
}
